import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSubscribeAiChatEventById } from './subscribeAiChatEventById'
import type { AiChatEvent } from 'poc-ai-biz-irdb_domain-model'
import { useLoggingEffect } from '../../../../pages/_shared/utils'
import { toast } from 'sonner'

export const useStreamingAiChatEvents = (aiChatEventIds: string[]) => {
  // ストリーミングイベントの状態管理
  const [aiChatEvents, setAiChatEvents] = useState<Map<string, AiChatEvent>>(new Map())
  const [errors, setErrors] = useState<string[]>([])

  // アクティブなストリーミング接続の管理
  const activeStreamsRef = useRef<Map<string, AbortController>>(new Map())
  const subscribeAiChatEventById = useSubscribeAiChatEventById()

  // assistantMessageイベントのストリーミング開始
  useLoggingEffect(
    () => {
      const newAssistantEventIds = aiChatEventIds.filter((eventId: string) => !activeStreamsRef.current.has(eventId))
      if (newAssistantEventIds.length === 0) {
        return
      }

      for (const aiChatEventId of newAssistantEventIds) {
        // 既に同じイベントIDでストリーミングが開始されている場合はスキップ
        if (activeStreamsRef.current.has(aiChatEventId)) {
          continue
        }

        const abortController = new AbortController()
        activeStreamsRef.current.set(aiChatEventId, abortController)

        void (async () => {
          try {
            const stream = subscribeAiChatEventById({ aiChatEventId })

            for await (const event of stream) {
              if (abortController.signal.aborted) break

              // console.log('Received streaming update for event:', aiChatEventId, event)
              setAiChatEvents((prev) => {
                const newMap = new Map(prev)
                newMap.set(aiChatEventId, event)
                return newMap
              })
            }
          } catch (error) {
            if (abortController.signal.aborted || (error instanceof Error && error.message.includes('Aborted'))) {
              console.log(`Streaming for event ${aiChatEventId} aborted`)
            } else {
              console.error(`Error streaming event ${aiChatEventId}:`, error)
              const errorMessage = error instanceof Error ? error.message : 'Unknown error'
              setErrors((prev) => [...prev, `Error streaming event ${aiChatEventId}: ${errorMessage}`])
              toast.error('メッセージの受信に失敗しました', {
                description: errorMessage,
              })
            }
          }
        })()
      }
    },
    [aiChatEventIds],
    'Subscribe to AI chat events by IDs',
  )

  // ストリーミング接続のクリーンアップ
  const cleanupStreams = useCallback(() => {
    activeStreamsRef.current.forEach((controller) => {
      controller.abort()
    })
    activeStreamsRef.current.clear()
    setAiChatEvents(new Map())
    setErrors([])
  }, [])

  // コンポーネントのアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      cleanupStreams()
    }
  }, [cleanupStreams])

  // イベントIDが変更された時にストリーミングをリセット
  useEffect(() => {
    // 不要になったストリーミング接続を停止
    const currentEventIds = new Set(aiChatEventIds)
    const controllersToAbort: string[] = []

    activeStreamsRef.current.forEach((controller, eventId) => {
      if (!currentEventIds.has(eventId)) {
        controllersToAbort.push(eventId)
      }
    })

    // 別のループでabortとdeleteを実行（forEach中のdeleteを避ける）
    controllersToAbort.forEach((eventId) => {
      const controller = activeStreamsRef.current.get(eventId)
      if (controller) {
        controller.abort()
        activeStreamsRef.current.delete(eventId)
      }
    })

    setAiChatEvents((prev) => {
      const newMap = new Map()
      prev.forEach((event, eventId) => {
        if (currentEventIds.has(eventId)) {
          newMap.set(eventId, event)
        }
      })
      return newMap
    })
  }, [aiChatEventIds])

  const data = useMemo(() => Array.from(aiChatEvents.values()).sort((a, b) => a.eventIndex - b.eventIndex), [aiChatEvents])

  return {
    data,
    errors,
  }
}
